// Multi-day range modes for the booking views (OTC-166).
//
// Every booking view (List, Calendar, Resource calendar, Kanban) resolves its
// date window, labels and persisted range through this module so week starts,
// month grids and date formatting stay identical across views. All dates are
// 'YYYY-MM-DD' wall-clock strings; ISO timestamps are accepted and truncated.
import moment from 'moment'
import { date as formatDate } from '@/plugins/dates'

export type BookingRange = 'day' | 'week' | 'month' | 'rolling-30' | 'custom'
export type BookingView = 'list' | 'calendar' | 'resource' | 'kanban'

export interface RangeWindow {
  startDate: string
  endDate: string
}

export interface RangeState {
  range: BookingRange
  customRange: string[]
}

export interface GroupDisplay {
  date_format?: string
  show_day_of_week?: boolean | number
}

// One card in a Week view (Calendar columns or Resource matrix).
export interface RangeCardItem {
  key: string
  date: string
  rowKey?: string | null
  sortKey: string
  timeLabel: string
  title: string
  subtitle: string
  groupSize: number
  statusLabel: string
  statusColor: string
  statusTextColor: string
  status?: string
  isAllDay?: boolean
  endKey?: string        // 'HH:mm:ss' end, used by the week time grid
  isMultiDay?: boolean   // spans more than one date; shown in the all-day strip
  signedCount?: number   // signed waivers on an unbooked slot (OTC-3117); 0 or absent on booked rows
  payload: unknown
}

// A signed waiver never changes a slot's status; unbooked slots carry it as an indicator.
export function showsSignedIndicator(item: Pick<RangeCardItem, 'status' | 'signedCount'>): boolean {
  return (item.signedCount || 0) > 0 && !['booked', 'completed', 'requested'].includes(item.status || '')
}

// Emitted by the week time grid when a booking is dragged to a new time or day
export interface WeekMove {
  payload: unknown
  date: string
  startMinutes: number
  endMinutes: number
  dateChanged: boolean
}

export interface TimedItem { item: RangeCardItem; start: number; end: number }
export interface LaidOutItem extends TimedItem { column: number; columns: number }
export interface OverflowGroup { start: number; end: number; items: RangeCardItem[] }

// Minutes since midnight for 'HH:mm:ss'
export function minutesOf(hhmmss: string | undefined | null): number {
  if (!hhmmss) return 0
  const [h, m] = String(hhmmss).split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

// Side-by-side layout for overlapping timed items in one day column. Items that
// overlap (transitively) form a cluster; each gets the first free column. When a
// cluster needs more than maxColumns, the first maxColumns - 1 columns render and
// the rest collapse into one overflow group occupying the last column.
export function layoutDay(items: TimedItem[], maxColumns = 3): { placed: LaidOutItem[]; overflow: OverflowGroup[] } {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end)
  const placed: LaidOutItem[] = []
  const overflow: OverflowGroup[] = []
  let cluster: { item: TimedItem; column: number }[] = []
  let clusterEnd = -1
  let columnEnds: number[] = []

  const flush = () => {
    if (cluster.length === 0) return
    const columns = columnEnds.length
    if (columns <= maxColumns) {
      cluster.forEach(c => placed.push({ ...c.item, column: c.column, columns }))
    } else {
      const shown = cluster.filter(c => c.column < maxColumns - 1)
      const hidden = cluster.filter(c => c.column >= maxColumns - 1)
      shown.forEach(c => placed.push({ ...c.item, column: c.column, columns: maxColumns }))
      overflow.push({
        start: Math.min(...hidden.map(c => c.item.start)),
        end: Math.max(...hidden.map(c => c.item.end)),
        items: hidden.map(c => c.item.item)
      })
    }
    cluster = []
    columnEnds = []
    clusterEnd = -1
  }

  for (const t of sorted) {
    if (cluster.length > 0 && t.start >= clusterEnd) flush()
    let column = columnEnds.findIndex(end => end <= t.start)
    if (column === -1) {
      column = columnEnds.length
      columnEnds.push(t.end)
    } else {
      columnEnds[column] = t.end
    }
    cluster.push({ item: t, column })
    clusterEnd = Math.max(clusterEnd, t.end)
  }
  flush()
  return { placed, overflow }
}

// Matches the lambda caps: 45 days of slot detail, 42 days (6 week rows) of month summary
export const MAX_RANGE_DAYS = 45
export const MAX_SUMMARY_DAYS = 42

export const RANGE_LABELS: Record<BookingRange, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  'rolling-30': 'Rolling 30 days',
  custom: 'Custom range'
}

export const VIEW_RANGES: Record<BookingView, BookingRange[]> = {
  list: ['day', 'week', 'rolling-30', 'custom'],
  kanban: ['day', 'week', 'rolling-30', 'custom'],
  calendar: ['day', 'week', 'month'],
  resource: ['day', 'week', 'month']
}

export const RANGE_STORAGE_KEYS: Record<BookingView, { range: string; custom: string }> = {
  kanban: { range: 'kanban_range', custom: 'kanban_custom_range' },
  list: { range: 'bookings_list_range', custom: 'bookings_list_custom_range' },
  calendar: { range: 'bookings_calendar_range', custom: 'bookings_calendar_custom_range' },
  resource: { range: 'bookings_resource_range', custom: 'bookings_resource_custom_range' }
}

const ISO_DAY = 'YYYY-MM-DD'
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const STATUS_LABELS: Record<string, string> = {
  booked: 'Booked',
  completed: 'Completed',
  requested: 'Requested',
  available: 'Available',
  blocked: 'Blocked',
  expired: 'Passed',
  call_to_book: 'Call to book',
  requestable: 'Requestable',
  cancelled: 'Cancelled'
}

function toDay(value: string | null | undefined): string {
  return value ? String(value).substring(0, 10) : ''
}

function isValidDay(value: string | null | undefined): boolean {
  const v = toDay(value)
  return DATE_RE.test(v) && moment(v, ISO_DAY, true).isValid()
}

function addDays(iso: string, days: number): string {
  return moment(iso, ISO_DAY).add(days, 'days').format(ISO_DAY)
}

function defaultStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch (e) {
    return null
  }
}

export function rangeOptions(view: BookingView): { value: BookingRange; label: string }[] {
  return VIEW_RANGES[view].map(value => ({ value, label: RANGE_LABELS[value] }))
}

// Accepts the group value (0 to 6, 0 = Sunday) or the Element UI value (1 to 7,
// 7 = Sunday). Returns the moment weekday (0 = Sunday). Missing defaults to
// Sunday, matching the date pickers (BaseDateNavigator uses first_day_of_week || 7).
export function toMomentWeekday(firstDayOfWeek: number | string | null | undefined): number {
  if (firstDayOfWeek === null || firstDayOfWeek === undefined || firstDayOfWeek === '') return 0
  const n = Number(firstDayOfWeek)
  return Number.isNaN(n) ? 0 : ((n % 7) + 7) % 7
}

export function weekStart(anchorIso: string, firstDayOfWeek: number | string | null | undefined): string {
  const anchor = moment(toDay(anchorIso), ISO_DAY)
  const back = (anchor.day() - toMomentWeekday(firstDayOfWeek) + 7) % 7
  return anchor.subtract(back, 'days').format(ISO_DAY)
}

export function monthGridWindow(anchorIso: string, firstDayOfWeek: number | string | null | undefined): RangeWindow {
  const anchor = moment(toDay(anchorIso), ISO_DAY)
  const first = anchor.clone().startOf('month').format(ISO_DAY)
  const last = anchor.clone().endOf('month').format(ISO_DAY)
  return {
    startDate: weekStart(first, firstDayOfWeek),
    endDate: addDays(weekStart(last, firstDayOfWeek), 6)
  }
}

export function computeRangeWindow(
  range: BookingRange,
  anchorIso: string,
  customRange: string[],
  firstDayOfWeek: number | string | null | undefined,
  todayIso: string = moment().format(ISO_DAY)
): RangeWindow {
  const anchor = toDay(anchorIso) || todayIso
  if (range === 'day') return { startDate: anchor, endDate: anchor }
  if (range === 'month') return monthGridWindow(anchor, firstDayOfWeek)
  if (range === 'rolling-30') return { startDate: todayIso, endDate: addDays(todayIso, 29) }
  if (range === 'custom' && Array.isArray(customRange) && customRange.length === 2 &&
      isValidDay(customRange[0]) && isValidDay(customRange[1]) && toDay(customRange[0]) <= toDay(customRange[1])) {
    return { startDate: toDay(customRange[0]), endDate: toDay(customRange[1]) }
  }
  const start = weekStart(anchor, firstDayOfWeek)
  return { startDate: start, endDate: addDays(start, 6) }
}

export function daysInWindow(window: RangeWindow): string[] {
  const days: string[] = []
  if (!isValidDay(window.startDate) || !isValidDay(window.endDate)) return days
  for (let d = toDay(window.startDate); d <= toDay(window.endDate); d = addDays(d, 1)) {
    days.push(d)
  }
  return days
}

// True for an end time of exactly midnight ('00:00' or '00:00:00')
export function endsAtMidnight(endTime: string | null | undefined): boolean {
  return !!endTime && /^00:00(:00)?$/.test(String(endTime).trim())
}

// OTC-1586: the operating day. company_groups.day_rollover_time ('HH:mm:ss',
// default '00:00:00') is where a venue's day starts. Viewing day D covers
// D at the boundary through D+1 at the boundary: always 24 hours. Slots keep
// their real booking_date; the window only decides what a Day view shows.

// Boundary minutes since midnight from the group row value. Missing, empty
// or malformed values mean midnight so every view behaves as before.
export function boundaryMinutes(value: string | null | undefined): number {
  if (!value) return 0
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(value).trim())
  if (!m) return 0
  const minutes = Number(m[1]) * 60 + Number(m[2])
  return minutes >= 0 && minutes < 1440 ? minutes : 0
}

export interface OperatingWindow {
  startDate: string     // D
  endDate: string       // D when the boundary is midnight, otherwise D+1
  startMinutes: number  // boundary, as minutes since D 00:00
  endMinutes: number    // boundary + 1440
}

export function operatingWindow(dayIso: string, boundary: number): OperatingWindow {
  const day = toDay(dayIso)
  return {
    startDate: day,
    endDate: boundary > 0 ? addDays(day, 1) : day,
    startMinutes: boundary,
    endMinutes: boundary + 1440
  }
}

// Minutes since 00:00 of dayIso for a wall-clock date and time. A time on
// the next date is 1440 or more; a time on the previous date is negative.
// Calendar day offsets use Date.UTC on the date parts so a daylight saving
// change (a 23 or 25 hour day) never shifts the result.
function dayNumber(iso: string): number {
  const [y, m, d] = toDay(iso).split('-').map(Number)
  return Date.UTC(y, m - 1, d) / 86400000
}

export function extendedMinutes(dateIso: string, hhmmss: string | null | undefined, dayIso: string): number {
  const days = dayNumber(dateIso) - dayNumber(dayIso)
  return days * 1440 + minutesOf(hhmmss)
}

// extendedMinutes for a JS Date read on the wall clock (FullCalendar event starts and ends)
export function extendedMinutesOfDate(date: Date, dayIso: string): number {
  const iso = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0')
  const time = String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0') + ':00'
  return extendedMinutes(iso, time, dayIso)
}

export interface WindowSlot {
  startDate: string
  endDate?: string | null
  startTime?: string | null
  endTime?: string | null
  isAllDay?: boolean | number | null
}

// True when the slot occupies any part of the window [startMinutes, endMinutes)
// measured from D 00:00. An end before or equal to the start is treated as a
// zero-length slot at its start. A slot ending at exactly the window start
// (for example 23:00 to 00:00 viewed on the next date) is not in the window.
// All-day slots follow calendar dates: in the window when D lies between
// their start and end dates.
export function slotInWindow(slot: WindowSlot, window: OperatingWindow): boolean {
  const start = toDay(slot.startDate)
  if (!isValidDay(start)) return false
  const rawEnd = toDay(slot.endDate)
  const end = isValidDay(rawEnd) && rawEnd >= start ? rawEnd : start
  if (slot.isAllDay) {
    return start <= window.startDate && end >= window.startDate
  }
  const startMin = extendedMinutes(start, slot.startTime, window.startDate)
  let endMin = extendedMinutes(end, slot.endTime, window.startDate)
  if (endMin <= startMin) endMin = startMin + 1
  return startMin < window.endMinutes && endMin > window.startMinutes
}

// Real date and 'HH:mm:ss' for a grid position expressed in minutes since D 00:00.
export function fromExtendedMinutes(dayIso: string, minutes: number): { date: string; time: string } {
  const days = Math.floor(minutes / 1440)
  const rest = minutes - days * 1440
  const h = Math.floor(rest / 60)
  const m = rest % 60
  return {
    date: addDays(toDay(dayIso), days),
    time: String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') + ':00'
  }
}

// FullCalendar duration string for minutes since D 00:00; hours may exceed 24 ('26:00:00').
export function durationString(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') + ':00'
}

// Booking view rows as the lambdas return them (booking_date may be an ISO timestamp).
export function bookingInWindow(
  row: { booking_date: string; end_date?: string | null; start_time?: string | null; end_time?: string | null; is_all_day?: boolean | number | null },
  window: OperatingWindow
): boolean {
  return slotInWindow({ startDate: row.booking_date, endDate: row.end_date, startTime: row.start_time, endTime: row.end_time, isAllDay: row.is_all_day }, window)
}

// Resource slot rows as the lambdas return them.
export function resourceSlotInWindow(
  row: { start_date: string; end_date?: string | null; start_time?: string | null; end_time?: string | null },
  window: OperatingWindow
): boolean {
  return slotInWindow({ startDate: row.start_date, endDate: row.end_date, startTime: row.start_time, endTime: row.end_time }, window)
}

// Every day a slot spans that falls inside the window. An end date before the
// start date is treated as a single-day slot. A slot ending at exactly midnight
// (end_date the next day, end_time 00:00) belongs only to the days before it.
export function spannedDaysInWindow(
  bookingDate: string,
  endDate: string | null | undefined,
  window: RangeWindow,
  endTime?: string | null
): string[] {
  const start = toDay(bookingDate)
  if (!isValidDay(start)) return []
  const rawEnd = toDay(endDate)
  let end = isValidDay(rawEnd) && rawEnd >= start ? rawEnd : start
  if (end > start && endsAtMidnight(endTime)) end = addDays(end, -1)
  const from = start > window.startDate ? start : window.startDate
  const to = end < window.endDate ? end : window.endDate
  if (from > to) return []
  return daysInWindow({ startDate: from, endDate: to })
}

export function formatRangeDay(iso: string, group: GroupDisplay): string {
  const day = toDay(iso)
  const prefix = group.show_day_of_week ? moment(day, ISO_DAY).format('ddd') + ', ' : ''
  return prefix + formatDate(day, group.date_format || 'MM-DD-YYYY')
}

export function formatWindowLabel(window: RangeWindow, group: GroupDisplay): string {
  const fmt = group.date_format || 'MM-DD-YYYY'
  return `${formatDate(window.startDate, fmt)} – ${formatDate(window.endDate, fmt)}`
}

export function formatRangeTime(hhmmss: string, timeFormat: string | undefined): string {
  const m = moment(hhmmss, 'HH:mm:ss', true)
  const loose = m.isValid() ? m : moment(hhmmss, 'HH:mm')
  if (!hhmmss || !loose.isValid()) return ''
  return loose.format(timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A')
}

// Week and Month card colors: the status color map (system overrides, custom statuses, then the
// default status colors, e.g. completed green), the same as the drawer, List and Kanban, plus the
// Day view's two warning colors: a cancelled transaction is faded, an incomplete booking is orange.
export function rangeColors(
  booking: { status: string; transaction_status?: number | null; transaction_id?: number | null; is_public_booking?: number | boolean | null },
  getStatusColor: (status: string) => { color: string; text_color: string }
): { backgroundColor: string; textColor: string } {
  if (booking.transaction_status === 2) return { backgroundColor: 'rgba(221,221,221,0.25)', textColor: '#FFFFFF' }
  if (booking.status === 'booked' && booking.transaction_id === null && !booking.is_public_booking) {
    return { backgroundColor: '#c77d30', textColor: '#FFFFFF' }
  }
  const { color, text_color } = getStatusColor(booking.status)
  return { backgroundColor: color, textColor: text_color }
}

// Calendar Day card colors. An operator's saved color (system override or custom status) wins;
// completed falls back to the status color map default (green), never the booked blue.
export function dayColors(
  booking: { status: string; transaction_status?: number | null; transaction_id?: number | null; is_public_booking?: number | boolean | null },
  hasSavedColor: (status: string) => boolean,
  getStatusColor: (status: string) => { color: string; text_color: string }
): { backgroundColor: string; textColor: string } {
  let backgroundColor = '#ff8d72' // Default fallback
  let textColor = '#FFFFFF' // Default text color
  const status = booking.status

  // Special cases that override status colors (preserved for visual distinction)
  if (booking.transaction_status === 2) {
    // Cancelled/refunded transaction - always faded
    backgroundColor = 'rgba(221,221,221,0.25)'
  } else if (status === 'booked' && booking.transaction_id === null && !booking.is_public_booking) {
    // Incomplete booking - payment likely failed
    backgroundColor = '#c77d30'
  } else if (hasSavedColor(status) || status === 'completed') {
    const statusColor = getStatusColor(status)
    backgroundColor = statusColor.color
    textColor = statusColor.text_color
  } else if (status === 'available') {
    backgroundColor = '#E3E3E3'
    textColor = '#1E1E2F'
  } else if (status === 'blocked') {
    backgroundColor = '#fd5d93'
  } else if (status === 'expired') {
    backgroundColor = 'rgba(221,221,221,0.25)'
  } else if (status === 'booked' || status === 'requested') {
    backgroundColor = status === 'requested' ? '#00D6B4' : '#1d8cf8'
    textColor = status === 'requested' ? '#1E1E2F' : textColor
  } else if (status === 'requestable') {
    backgroundColor = '#FFD600'
    textColor = '#1E1E2F'
  }
  return { backgroundColor, textColor }
}

// Compact month-view time: '6pm', '11:30am', or '18:00' for 24-hour groups
export function formatCompactTime(hhmmss: string, timeFormat: string | undefined): string {
  const m = moment(hhmmss, 'HH:mm:ss')
  if (!hhmmss || !m.isValid()) return ''
  if (timeFormat === 'HH:mm') return m.format('HH:mm')
  return m.minutes() === 0 ? m.format('ha') : m.format('h:mma')
}

export function rangeStatusLabel(status: string, customStatusMap?: Map<string, { display_name?: string }>): string {
  const custom = customStatusMap && customStatusMap.get ? customStatusMap.get(status) : null
  if (custom && custom.display_name) return custom.display_name
  return STATUS_LABELS[status] || status
}

// A restorable custom range: two valid dates, in order, at most MAX_RANGE_DAYS long (the lambda cap)
export function isAllowedCustomRange(start: string | null | undefined, end: string | null | undefined): boolean {
  if (!isValidDay(start) || !isValidDay(end)) return false
  const from = toDay(start)
  const to = toDay(end)
  if (from > to) return false
  return daysInWindow({ startDate: from, endDate: to }).length <= MAX_RANGE_DAYS
}

// Month fill mode: how many lines of lineHeight fit in `available` px with `gap` between lines
export function fitLineCount(available: number, lineHeight: number, gap: number, tolerance = 0): number {
  if (!(lineHeight > 0)) return 1
  return Math.max(1, Math.floor((available + gap + tolerance) / (lineHeight + gap)))
}

// Split a day's items for a cell that fits maxLines lines: all of them when they fit,
// otherwise the first maxLines - 1 plus an "N more" line (none shown when only 1 line fits)
export function splitVisible<T>(items: T[], maxLines: number): { visible: T[]; hidden: number } {
  if (items.length <= maxLines) return { visible: items, hidden: 0 }
  const visible = items.slice(0, Math.max(0, maxLines - 1))
  return { visible, hidden: items.length - visible.length }
}

export function readRangeState(view: BookingView, search: string, storage: Storage | null = defaultStorage()): RangeState {
  const params = new URLSearchParams(search || '')
  const allowed = VIEW_RANGES[view]
  const keys = RANGE_STORAGE_KEYS[view]

  let range: BookingRange = 'day'
  const urlRange = params.get('range') as BookingRange | null
  if (urlRange && allowed.includes(urlRange)) {
    range = urlRange
  } else {
    try {
      const stored = storage ? (storage.getItem(keys.range) as BookingRange | null) : null
      if (stored && allowed.includes(stored)) range = stored
    } catch (e) { /* storage unavailable */ }
  }

  const today = moment().format(ISO_DAY)
  let customRange = [today, addDays(today, 6)]
  const urlStart = params.get('custom_start')
  const urlEnd = params.get('custom_end')
  if (isAllowedCustomRange(urlStart, urlEnd)) {
    customRange = [toDay(urlStart), toDay(urlEnd)]
  } else {
    try {
      const stored = storage ? JSON.parse(storage.getItem(keys.custom) || 'null') : null
      if (Array.isArray(stored) && stored.length === 2 && isAllowedCustomRange(stored[0], stored[1])) {
        customRange = [toDay(stored[0]), toDay(stored[1])]
      }
    } catch (e) { /* corrupt or unavailable */ }
  }

  return { range, customRange }
}

export function applyRangeToQuery(query: Record<string, any>, state: RangeState): Record<string, any> {
  const next: Record<string, any> = { ...query }
  delete next.range
  delete next.custom_start
  delete next.custom_end
  if (state.range === 'day') return next
  next.range = state.range
  if (state.range === 'custom' && state.customRange.length === 2) {
    next.custom_start = state.customRange[0]
    next.custom_end = state.customRange[1]
  }
  return next
}

export function persistRangeState(view: BookingView, state: RangeState, storage: Storage | null = defaultStorage()): void {
  if (!storage) return
  const keys = RANGE_STORAGE_KEYS[view]
  try {
    if (state.range === 'day') storage.removeItem(keys.range)
    else storage.setItem(keys.range, state.range)
    if (state.range === 'custom' && state.customRange.length === 2) {
      storage.setItem(keys.custom, JSON.stringify(state.customRange))
    }
  } catch (e) { /* storage unavailable */ }
}
